import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';

interface Props {
  children: JSX.Element;
}

/**
 * Gate for /admin/*. The token claim `admin:true` is the source of truth — the
 * backend issues it only when the user's email matches ADMIN_EMAIL, so any
 * client-side spoofing of `admin` in the store would still fail server-side.
 */
export function AdminRoute({ children }: Props): JSX.Element {
  const { t } = useTranslation();
  const { token, status, admin, user } = useAuth();
  const location = useLocation();

  // Token in storage, /me still resolving — same placeholder pattern as
  // ProtectedRoute so we don't flash a redirect before claims settle.
  if (token && status === 'verifying') {
    return (
      <main className="results-loading" role="status" aria-live="polite">
        {t('auth.checkingSignIn')}
      </main>
    );
  }

  // First-boot guest mint in flight.
  if (!token && status === 'loading' && !user) {
    return (
      <main className="results-loading" role="status" aria-live="polite">
        {t('auth.settingUpSession')}
      </main>
    );
  }

  // Unauthenticated → send to login with redirect-back state.
  if (!token && status !== 'authenticated') {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: { pathname: location.pathname, search: location.search } }}
      />
    );
  }

  // Authenticated but not admin → bounce to home. We deliberately don't reveal
  // that an admin panel exists.
  if (!admin) {
    return <Navigate to="/" replace />;
  }

  return children;
}
